/* eslint jsx-a11y/anchor-is-valid: 0 */

import React, {useState, useEffect, useCallback} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  FormSelect,
  FormInput,
  ButtonGroup,
  Button,
  FormCheckbox,
  Modal,
  ModalBody,
  ModalHeader,
  Form
} from "shards-react";
import { accessCheck } from "../../utils/tools";
import moment from 'moment-timezone';
import firebase from 'firebase/compat/app';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import DownloadButton from "../../components/common/DownloadButton";
import PageTitle from "../../components/common/PageTitle";
import Pagination from "../../components/common/Pagination";
import ActivityIndicator from "../../components/common/ActivityIndicator";
import CheckinFeatureModal from "../../components/common/CheckinFeatureModal";
import generatePDF, { Resolution, Margin } from 'react-to-pdf';
import DatePicker from "react-datepicker";
import { startOfWeek, endOfWeek } from "date-fns";
import { GoogleMap, LoadScript, Marker, InfoWindow, HeatmapLayer, Polyline } from '@react-google-maps/api';

import "react-datepicker/dist/react-datepicker.css";

// Define marker icon colors for different types
const markerIcons = {
  authorizedLocation: {
    path: "M 12,2 C 8.1340068,2 5,5.1340068 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.8659932 -3.134007,-7 -7,-7 z",
    fillColor: "#21AA73", // EnforcePlus green
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: "#ffffff",
    scale: 2.2, // Make location pins slightly larger
    anchor: { x: 12, y: 22 },
  },
  checkinPin: {
    path: "M 12,2 C 8.1340068,2 5,5.1340068 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.8659932 -3.134007,-7 -7,-7 z",
    fillColor: "#E6492E", // Red for check-ins
    fillOpacity: 1,
    strokeWeight: 1,
    strokeColor: "#ffffff",
    scale: 1.5, // Smaller for check-ins
    anchor: { x: 12, y: 22 },
  }
};

// Debug environment variables
console.log("Environment variables debug:");
console.log("REACT_APP_GOOGLE_MAPS_API_KEY:", process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
console.log("All env vars:", process.env);

const mapContainerStyle = {
  width: '100%',
  height: '600px'
};

// Add a helper function to calculate distance between two coordinates in meters
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (
    lat1 === undefined || lon1 === undefined || 
    lat2 === undefined || lon2 === undefined ||
    isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)
  ) {
    return null;
  }
  
  const R = 6371000; // Radius of the earth in meters
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in meters
  
  return distance;
};

// Format distance for display
const formatDistance = (distance) => {
  if (distance === null) return 'Unknown';
  
  if (distance < 1000) {
    return `${Math.round(distance)} meters`;
  } else {
    return `${(distance / 1000).toFixed(2)} km`;
  }
};

// Add a function to safely check if google maps is loaded with visualization
const isGoogleMapsLoaded = () => {
  return window.google && 
         window.google.maps && 
         window.google.maps.visualization &&
         window.google.maps.visualization.HeatmapLayer;
};

const Checkins = (props) => {
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(startOfWeek(new Date()));
  const [endDate, setEndDate] = useState(endOfWeek(new Date()));
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(!searchParams.get("view") ? 'all' : searchParams.get("view"));
  const [client, setClient] = useState('');
  const [location, setLocation] = useState('all');
  const [deviceType, setDeviceType] = useState('all');
  const [sortBy, setSortBy] = useState('dateDesc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [endablePdfMode, setEndablePdfMode] = useState(false);
  // Add pdfTableStyles state
  const [pdfTableStyles, setPdfTableStyles] = useState({});
  const [viewMode, setViewMode] = useState('table'); // Now just 'table' or 'map'
  const [heatMapEnabled, setHeatMapEnabled] = useState(false); // New toggle for heat map within map view
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 37.7749, lng: -122.4194 }); // Default center (San Francisco)
  const [mapZoom, setMapZoom] = useState(10);
  const [selectedUser, setSelectedUser] = useState('');
  const [filterType, setFilterType] = useState('client');
  const [heatMapIntensity, setHeatMapIntensity] = useState(20);
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [proximityLine, setProximityLine] = useState(null);
  const [mapsLibraryLoaded, setMapsLibraryLoaded] = useState(false);
  
  // Add state to track user role
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userClientId, setUserClientId] = useState('');
  
  const [allowCheckIn, setAllowCheckIn] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  
  // Add subtitle based on filter type and selection
  const subtitle = filterType === 'client' 
    ? (client ? `Client: ${props.clients?.find(c => c.id === client)?.name || client}` : 'Select a client')
    : (selectedUser ? `User: ${props.users?.find(u => u.id === selectedUser)?.name || selectedUser}` : 'Select a user');

  // Get all the unique device types from checkins for the filter dropdown
  const deviceTypes = [...new Set(checkins.map(checkin => checkin.deviceType || 'Unknown'))];

  const csvHeader = [
    'Name',
    'Date/Time',
    'Location',
    'Status',
    'Device Type',
    'Coordinates',
    'Device Model',
    'Operating System'
  ];

  // Update the clientLocations memoized value to work with either client or selectedUser
  const clientLocations = React.useMemo(() => {
    if ((!client || client === '') && !selectedUser) return [];
    
    if (selectedUser && props.locations) {
      // If user is selected, find their client and filter locations by that
      const userDoc = props.users?.find(u => u.id === selectedUser);
      const userClient = userDoc?.client || '';
      return props.locations.filter(loc => loc.client === userClient);
    }
    
    if (client && props.locations) {
      return props.locations.filter(loc => loc.client === client);
    }
    
    return [];
  }, [client, selectedUser, props.locations, props.users]);

  // Add a helper function to safely convert any timestamp format to a JavaScript Date
  const convertToDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) return value;

    if (value.toDate && typeof value.toDate === 'function') {
      try { return value.toDate(); } catch (e) {}
    }

    if (typeof value === 'number') {
      return value < 2000000000 ? new Date(value * 1000) : new Date(value);
    }

    if (typeof value === 'string') {
      try {
        const d = new Date(value);
        if (!isNaN(d.getTime())) return d;
      } catch (e) {}
    }

    return null;
  };

  // Add deep inspection function before fetchCheckins
  const inspectObject = (obj, prefix = '') => {
    if (!obj || typeof obj !== 'object') {
      console.log(`${prefix}Value: ${obj}`);
      return;
    }
    
    // Limit the recursion depth to avoid infinite loops
    if (prefix.split('.').length > 5) {
      console.log(`${prefix}[Max depth reached]`);
      return;
    }
    
    const keys = Object.keys(obj);
    console.log(`${prefix}Keys: ${keys.join(', ')}`);
    
    // Look specifically for coordinate-related fields
    const coordKeys = keys.filter(k => 
      k.toLowerCase().includes('coord') || 
      k.toLowerCase().includes('location') || 
      k.toLowerCase().includes('gps') || 
      k.toLowerCase().includes('position') || 
      k.toLowerCase() === 'lat' || 
      k.toLowerCase() === 'lng' ||
      k.toLowerCase() === 'latitude' || 
      k.toLowerCase() === 'longitude'
    );
    
    if (coordKeys.length > 0) {
      console.log(`${prefix}Potential coordinate fields: ${coordKeys.join(', ')}`);
      coordKeys.forEach(key => {
        const value = obj[key];
        if (value && typeof value === 'object') {
          console.log(`${prefix}${key}: ${JSON.stringify(value)}`);
        } else {
          console.log(`${prefix}${key}: ${value}`);
        }
      });
    }
    
    // Check for specifically shaped nested objects that might be coordinates
    keys.forEach(key => {
      const value = obj[key];
      if (value && typeof value === 'object') {
        const subKeys = Object.keys(value);
        
        // Check if this object has lat/lng or latitude/longitude
        const hasLatLng = (
          (subKeys.includes('lat') && subKeys.includes('lng')) || 
          (subKeys.includes('latitude') && subKeys.includes('longitude'))
        );
        
        if (hasLatLng) {
          console.log(`${prefix}Found coordinate-like object in ${key}: ${JSON.stringify(value)}`);
        }
        
        // Recursively inspect nested objects, but not arrays
        if (!Array.isArray(value)) {
          inspectObject(value, `${prefix}${key}.`);
        }
      }
    });
  };

  // Update the extractCoordinates function to handle more cases
  const extractCoordinates = (data) => {
    // Debug log for development
    console.log('Extracting coordinates from data:', {
      path: data.path || data.id || 'unknown',
      hasCoordinates: !!data.coordinates,
      hasLocationCoords: !!data.location_coords,
      hasGps: !!data.gps,
      hasCurrentPosition: !!data.current_position,
      hasLatLng: (data.latitude !== undefined && data.longitude !== undefined),
      hasPosition: !!data.position,
      hasGpsDetails: !!data.gpsDetails
    });
    
    // Deep inspection for debugging
    console.log('Detailed structure inspection:');
    inspectObject(data);

    // Case 1: Direct coordinates object
    if (data.coordinates && typeof data.coordinates === 'object') {
      console.log('Found coordinates object:', data.coordinates);
      if (data.coordinates.lat && data.coordinates.lng) {
        return data.coordinates;
      }
    }

    // Case 2: Coordinates in location_coords
    if (data.location_coords && typeof data.location_coords === 'object') {
      console.log('Found location_coords object:', data.location_coords);
      if (data.location_coords.lat && data.location_coords.lng) {
        return data.location_coords;
      }
    }
    
    // NEW Case: current_position with lon/lat format (common in your existing data)
    if (data.current_position && typeof data.current_position === 'object') {
      console.log('Found current_position object:', data.current_position);
      // If current_position has lat and lon (instead of lng)
      if (data.current_position.lat && data.current_position.lon) {
        return {
          lat: data.current_position.lat,
          lng: data.current_position.lon // Convert lon to lng format
        };
      }
      // Also check if it uses lat/lng format
      if (data.current_position.lat && data.current_position.lng) {
        return data.current_position;
      }
    }

    // Case 3: Coordinates in gps object
    if (data.gps) {
      console.log('Found gps object:', data.gps);
      if (typeof data.gps === 'object') {
        // If gps is an object with lat/lng
        if (data.gps.lat && data.gps.lng) {
          return data.gps;
        }
        
        // If gps has latitude/longitude
        if (data.gps.latitude && data.gps.longitude) {
          return {
            lat: data.gps.latitude,
            lng: data.gps.longitude
          };
        }
        
        // If gps has lat/lon format
        if (data.gps.lat && data.gps.lon) {
          return {
            lat: data.gps.lat,
            lng: data.gps.lon
          };
        }
      }
    }

    // Case 4: Direct latitude/longitude properties
    if (data.latitude !== undefined && data.longitude !== undefined) {
      console.log('Found direct lat/long properties:', { lat: data.latitude, lng: data.longitude });
      return {
        lat: data.latitude,
        lng: data.longitude
      };
    }

    // Case 5: Position object
    if (data.position && typeof data.position === 'object') {
      console.log('Found position object:', data.position);
      if (data.position.coords) {
        return {
          lat: data.position.coords.latitude,
          lng: data.position.coords.longitude
        };
      }
    }

    // Case 6: GPS details object
    if (data.gpsDetails && typeof data.gpsDetails === 'object') {
      console.log('Found gpsDetails object:', data.gpsDetails);
      if (data.gpsDetails.latitude && data.gpsDetails.longitude) {
        return {
          lat: data.gpsDetails.latitude,
          lng: data.gpsDetails.longitude
        };
      }
    }
    
    // Case 7: Check inside a location field if it exists
    if (data.location && typeof data.location === 'object') {
      console.log('Found location object:', data.location);
      // Check if location has coordinates
      if (data.location.coordinates) {
        return data.location.coordinates;
      }
      
      // Check if location has lat/lng
      if (data.location.lat && data.location.lng) {
        return data.location;
      }
      
      // Check if location has latitude/longitude
      if (data.location.latitude && data.location.longitude) {
        return {
          lat: data.location.latitude,
          lng: data.location.longitude
        };
      }
    }
    
    // Case 8: Check for deeply nested coordinates
    for (const key in data) {
      if (data[key] && typeof data[key] === 'object') {
        // Skip certain fields we already checked
        if (['coordinates', 'location_coords', 'gps', 'position', 'gpsDetails', 'location'].includes(key)) {
          continue;
        }
        
        // Check if this field has a coordinates property
        if (data[key].coordinates && typeof data[key].coordinates === 'object') {
          console.log(`Found nested coordinates in ${key}:`, data[key].coordinates);
          if (data[key].coordinates.lat && data[key].coordinates.lng) {
            return data[key].coordinates;
          }
        }
        
        // Check if this field has lat/lng directly
        if (data[key].lat && data[key].lng) {
          console.log(`Found nested lat/lng in ${key}:`, data[key]);
          return data[key];
        }
        
        // Check if this field has latitude/longitude
        if (data[key].latitude && data[key].longitude) {
          console.log(`Found nested latitude/longitude in ${key}:`, data[key]);
          return {
            lat: data[key].latitude,
            lng: data[key].longitude
          };
        }
      }
    }

    // No coordinates found
    console.log('No coordinates found in the data');
    return null;
  };

  // Add a helper function to extract device type right after extractCoordinates
  const extractDeviceType = (data) => {
    // Direct deviceType field
    if (data.deviceType) {
      return data.deviceType;
    }
    
    // Alternative field name
    if (data.device_type) {
      return data.device_type;
    }
    
    // Check in deviceInfo
    if (data.deviceInfo && data.deviceInfo.type) {
      return data.deviceInfo.type;
    }
    
    if (data.deviceInfo && data.deviceInfo.model) {
      return data.deviceInfo.model;
    }
    
    if (data.device_info && data.device_info.type) {
      return data.device_info.type;
    }
    
    if (data.device_info && data.device_info.model) {
      return data.device_info.model;
    }
    
    // Try to determine from device model or platform if available
    if (data.device_info && data.device_info.os) {
      return data.device_info.os;
    }
    
    if (data.deviceInfo && data.deviceInfo.os) {
      return data.deviceInfo.os;
    }
    
    // Look for device platform in the data
    if (data.platform) {
      return data.platform;
    }
    
    return 'Unknown';
  };

  const fetchCheckins = async () => {
    // For admin users or when impersonating a non-super user, ensure we use the correct client
    let clientIdToUse = client;
    
    if (isAdmin && userClientId) {
      clientIdToUse = userClientId;
      
      // If client doesn't match user's client, update it
      if (client !== clientIdToUse) {
        console.log(`Updating client selection to match admin user: ${clientIdToUse}`);
        setClient(clientIdToUse);
      }
    } else if (props.impersonated && props.impersonatedUser?.client && !isSuperUser) {
      // Override with impersonated user's client if impersonating non-super user
      clientIdToUse = props.impersonatedUser.client;
      console.log(`Impersonation active: Using client ${clientIdToUse} from impersonated user`);
      
      // If client doesn't match impersonated user's client, update it
      if (client !== clientIdToUse) {
        console.log(`Updating client selection to match impersonated user: ${clientIdToUse}`);
        setClient(clientIdToUse);
      }
    }
    
    // Prevent fetching if neither client nor user is selected
    // Super users need to explicitly select a client, admins use their assigned client
    if ((!clientIdToUse || clientIdToUse === '') && !selectedUser) {
      setCheckins([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Determine if we're filtering by user or client
      const isUserFiltered = !!selectedUser;
      const clientId = isUserFiltered 
        ? (props.users?.find(u => u.id === selectedUser)?.client || '')
        : clientIdToUse;
      
      console.log(`Fetching checkins for ${isUserFiltered ? 'user: ' + selectedUser : 'client: ' + clientId}`);
      const db = firebase.firestore();
      
      console.log("Looking for checkins in different collection structures...");
      
      // Try all possible data structures to find checkins
      let allCheckins = [];
      
      // 1. Try direct checkins collection (not under users)
      try {
        console.log("ATTEMPT 1: Looking for checkins as top-level collection...");
        const directCheckinsRef = db.collection('checkins');
        let directCheckinsQuery;
        
        if (isUserFiltered) {
          // Filter by user ID
          directCheckinsQuery = directCheckinsRef
            .where('userId', '==', selectedUser)
            .orderBy('timestamp', 'desc')
            .limit(50);
        } else {
          // Filter by client
          directCheckinsQuery = directCheckinsRef
            .where('client', '==', clientId)
            .orderBy('timestamp', 'desc')
            .limit(50);
        }
        
        const directCheckinsSnapshot = await directCheckinsQuery.get();
        console.log(`Found ${directCheckinsSnapshot.docs.length} checkins in top-level collection`);
        
        if (!directCheckinsSnapshot.empty) {
          console.log("DETAILED CHECK-IN ANALYSIS - TOP LEVEL COLLECTION");
          // Log the data structure of the first few documents
          directCheckinsSnapshot.docs.slice(0, 3).forEach((doc, idx) => {
            console.log(`\nDocument #${idx + 1} (${doc.id}):`);
            const data = doc.data();
            console.log('Raw data structure:');
            console.log(JSON.stringify(data, null, 2));
            console.log('\nFields analysis:');
            Object.keys(data).forEach(key => {
              console.log(`${key}: ${typeof data[key]} = ${JSON.stringify(data[key])}`);
            });
            
            // Look specifically for coordinates
            const coords = extractCoordinates(data);
            console.log(`Extracted coordinates: ${coords ? JSON.stringify(coords) : 'NONE'}`);
          });
          
          // Continue with regular processing...
          directCheckinsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const formattedCheckin = {
              id: doc.id,
              user: data.userId || data.user || '',
              userName: data.userName || 'Unknown User',
              created_at: convertToDate(data.created_at || data.timestamp),
              location_name: data.locationName || data.location_name || 'Unknown Location',
              location: data.locationId || data.location || '',
              client: data.client || client,
              status: data.status || 'complete',
              deviceType: extractDeviceType(data),
              coordinates: extractCoordinates(data),
              deviceInfo: data.deviceInfo || data.device_info || {}
            };
            allCheckins.push(formattedCheckin);
          });
        }
      } catch (error) {
        console.error("Error checking direct checkins collection:", error);
      }
      
      // 2. Try users/user/checkins (original approach with improved field checking)
      try {
        console.log("ATTEMPT 2: Looking for checkins under each user...");
        const usersRef = db.collection('users');
        let usersSnapshot;
        
        if (isUserFiltered) {
          // Just get the one user
          const userDoc = await usersRef.doc(selectedUser).get();
          if (userDoc.exists) {
            usersSnapshot = { docs: [userDoc] };
          } else {
            usersSnapshot = { docs: [] };
          }
        } else {
          // Filter users by client
          const usersQuery = usersRef.where('client', '==', clientId);
          usersSnapshot = await usersQuery.get();
        }
        
        console.log(`Found ${usersSnapshot.docs.length} users to check`);
        
        // Loop through each user to get their checkins
        for (const userDoc of usersSnapshot.docs) {
          // Get user data for reference
          const userData = userDoc.data();
          const userName = userData.name || userData.displayName || 'Unknown User';
          
          console.log(`Checking checkins for user: ${userName} (${userDoc.id})`);
          
          const checkinsRef = userDoc.ref.collection('checkins');
          
          // List all subcollections for this user
          try {
            const collections = await userDoc.ref.listCollections();
            console.log(`User ${userName} has ${collections.length} subcollections:`, 
              collections.map(c => c.id).join(', '));
          } catch (err) {
            console.log("Cannot list collections:", err);
          }
          
          try {
            // Try with just a limit first to see any data regardless of date
            const simpleQuery = checkinsRef.limit(10);
            const simpleSnapshot = await simpleQuery.get();
            
            if (!simpleSnapshot.empty) {
              console.log(`Found ${simpleSnapshot.docs.length} checkins for user without filters`);
              const sampleData = simpleSnapshot.docs[0].data();
              console.log("Sample data:", sampleData);
              
              // Now we know there's data, figure out the timestamp field
              const hasTimestamp = 'timestamp' in sampleData;
              const hasCreatedAt = 'created_at' in sampleData;
              const hasCreatedOn = 'created_on' in sampleData;
              const hasDate = 'date' in sampleData;
              
              console.log(`Field check: timestamp=${hasTimestamp}, created_at=${hasCreatedAt}, created_on=${hasCreatedOn}, date=${hasDate}`);
              
              // Use the field that exists
              let timestampField = 'timestamp';
              if (!hasTimestamp && hasCreatedAt) timestampField = 'created_at';
              if (!hasTimestamp && !hasCreatedAt && hasCreatedOn) timestampField = 'created_on';
              if (!hasTimestamp && !hasCreatedAt && !hasCreatedOn && hasDate) timestampField = 'date';
              
              console.log(`Using ${timestampField} as the timestamp field`);
              
              // Use the correct timestamp field for querying
              const checkinsQuery = checkinsRef
                .orderBy(timestampField, 'desc')
                .limit(50);
              
              const checkinsSnapshot = await checkinsQuery.get();
              
              console.log(`Found ${checkinsSnapshot.docs.length} checkins for user ${userName}`);
              
              // Process each checkin with flexible field mapping
              checkinsSnapshot.docs.forEach(checkinDoc => {
                const checkinData = checkinDoc.data();
                
                // Format the checkin with flexible field mapping
                const formattedCheckin = {
                  id: checkinDoc.id,
                  user: userDoc.id,
                  userName: userName,
                  created_at: convertToDate(checkinData.created_at || checkinData[timestampField]),
                  location_name: checkinData.locationName || checkinData.location_name || 'Unknown Location',
                  location: checkinData.locationId || checkinData.location || '',
                  client: userData.client || client,
                  status: checkinData.status || 'complete',
                  deviceType: extractDeviceType(checkinData),
                  coordinates: extractCoordinates(checkinData),
                  deviceInfo: checkinData.deviceInfo || checkinData.device_info || {}
                };
                
                allCheckins.push(formattedCheckin);
              });
            } else {
              console.log(`No checkins found for user: ${userName}`);
            }
          } catch (err) {
            console.error(`Error querying checkins for user ${userName}:`, err);
          }
        }
      } catch (error) {
        console.error("Error checking user-based checkins:", error);
      }
      
      // 3. Try a locations-based approach
      try {
        console.log("ATTEMPT 3: Looking for checkins under locations...");
        const locationsRef = db.collection('locations');
        const locationsQuery = locationsRef.where('client', '==', clientId);
        
        const locationsSnapshot = await locationsQuery.get();
        console.log(`Found ${locationsSnapshot.docs.length} locations for client ${clientId}`);
        
        for (const locationDoc of locationsSnapshot.docs) {
          const locationData = locationDoc.data();
          console.log(`Checking location: ${locationData.name} (${locationDoc.id})`);
          
          const checkinOptions = ['checkins', 'check_ins', 'check-ins', 'attendances'];
          
          for (const collectionName of checkinOptions) {
            try {
              const locCheckinsRef = locationDoc.ref.collection(collectionName);
              const locCheckinsSnapshot = await locCheckinsRef.limit(10).get();
              
              if (!locCheckinsSnapshot.empty) {
                console.log(`Found ${locCheckinsSnapshot.docs.length} checkins in location/${collectionName}`);
                console.log("Sample data:", locCheckinsSnapshot.docs[0].data());
                
                // Process location-based checkins
                locCheckinsSnapshot.docs.forEach(checkinDoc => {
                  const checkinData = checkinDoc.data();
                  
                  // Try to find a timestamp field
                  let checkinDate = convertToDate(
                    checkinData.created_at ||
                    checkinData.timestamp ||
                    checkinData.date
                  );
                  
                  const formattedCheckin = {
                    id: checkinDoc.id,
                    user: checkinData.userId || checkinData.user_id || 'unknown',
                    userName: checkinData.userName || checkinData.user_name || 'Unknown User',
                    created_at: checkinDate,
                      location_name: locationData.name,
                    location: locationDoc.id,
                    client: client,
                    status: checkinData.status || 'complete',
                    deviceType: extractDeviceType(checkinData),
                    coordinates: extractCoordinates(checkinData) || (locationData ? extractCoordinates(locationData) : null),
                    deviceInfo: checkinData.deviceInfo || checkinData.device_info || {}
                  };
                  
                  allCheckins.push(formattedCheckin);
                });
              }
            } catch (err) {
              console.log(`No ${collectionName} subcollection found for location ${locationData.name}`);
            }
          }
        }
      } catch (error) {
        console.error("Error checking location-based checkins:", error);
      }
      
      console.log(`Total checkins found across all attempts: ${allCheckins.length}`);
      
      // Deduplicate by content fingerprint (user + location + timestamp) to catch
      // the same logical checkin stored in multiple collections with different doc IDs
      const uniqueCheckins = [];
      const seenKeys = new Set();

      allCheckins.forEach(checkin => {
        const ts = checkin.created_at ? checkin.created_at.getTime() : 0;
        const contentKey = `${checkin.user}|${checkin.location}|${ts}`;
        if (!seenKeys.has(contentKey)) {
          seenKeys.add(contentKey);
          uniqueCheckins.push(checkin);
        }
      });
      
      console.log(`After deduplication: ${uniqueCheckins.length} unique checkins`);
      
      // Update map center if we have checkins with coordinates
      const checkinsWithCoordinates = uniqueCheckins.filter(c => c.coordinates && c.coordinates.lat && c.coordinates.lng);
      if (checkinsWithCoordinates.length > 0) {
        // Set center to the most recent checkin with coordinates
        setMapCenter(checkinsWithCoordinates[0].coordinates);
      }
      
      setCheckins(uniqueCheckins);

      // Add this debugging code
      console.log("CHECKINS DATA DETAIL:");
      uniqueCheckins.slice(0, 5).forEach((checkin, index) => {
        console.log(`Checkin #${index + 1}:`);
        console.log(`- User: ${checkin.userName} (${checkin.user})`);
        console.log(`- Location: ${checkin.location_name}`);
        console.log(`- Device Type: ${checkin.deviceType || 'NOT SET'}`);
        console.log(`- Coordinates: ${checkin.coordinates ? 
          `${JSON.stringify(checkin.coordinates)}` : 'NOT SET'}`);
        console.log(`- Raw data:`, checkin);
      });

    } catch (error) {
      console.error("Error fetching checkins:", error);
    } finally {
      setLoading(false);
    }
  };

  const createSampleCheckinData = async () => {
    if (!client && !selectedUser) {
      alert('Please select either a client or a user before creating sample data.');
      return;
    }

    if (!window.confirm('This will create sample checkin data for testing, including both authorized and unauthorized locations. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      console.log("Creating sample checkin data...");
      const db = firebase.firestore();
      
      // Get the target user - either the selected user or current user
      let targetUserId = selectedUser;
      
      if (!targetUserId) {
        // If filtering by client, use the current user
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) {
          console.error("No logged in user found");
          return;
        }
        targetUserId = currentUser.uid;
      }
      
      // Get the user document
      const userRef = db.collection('users').doc(targetUserId);
      const userDoc = await userRef.get();
      
      if (!userDoc.exists) {
        console.error("User document not found");
        return;
      }
      
      // Update user's client if client filtering and needed
      const userData = userDoc.data();
      if (client && userData.client !== client) {
        console.log(`Updating user's client to ${client}`);
        await userRef.update({ client: client });
      }
      
      // Create a checkins subcollection if it doesn't exist
      const checkinsRef = userRef.collection('checkins');
      
      // Create sample data
      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - (60 * 60 * 1000));
      const twoDaysAgo = new Date(now.getTime() - (2 * 24 * 60 * 60 * 1000));
      const threeDaysAgo = new Date(now.getTime() - (3 * 24 * 60 * 60 * 1000));
      
      // Determine which client to use
      const useClient = client || userData.client;
      
      // Filter locations for the selected client - these are AUTHORIZED locations
      const clientLocations = props.locations
        ? props.locations.filter(loc => loc.client === useClient).slice(0, 2)
        : [{ id: 'sample-loc-1', name: 'Sample Location 1', client: useClient }];
      
      // Find some locations from other clients - these are UNAUTHORIZED locations
      const unauthorizedLocations = props.locations
        ? props.locations.filter(loc => loc.client !== useClient).slice(0, 2)
        : [{ id: 'unauth-loc-1', name: 'Unauthorized Location 1', client: 'other-client' }];
      
      // If we don't have enough unauthorized locations, create fake ones
      if (unauthorizedLocations.length === 0) {
        unauthorizedLocations.push(
          { id: 'unauth-loc-1', name: 'Unauthorized Location 1', client: 'other-client' }
        );
      }
      
      console.log(`Using ${clientLocations.length} authorized locations and ${unauthorizedLocations.length} unauthorized locations for sample data`);
      
      const sampleData = [
        // Authorized locations
        {
          timestamp: firebase.firestore.Timestamp.fromDate(now),
          locationId: clientLocations[0]?.id || 'sample-loc-1',
          locationName: clientLocations[0]?.name || 'Sample Location 1',
          status: 'active',
          deviceType: 'iOS',
          coordinates: { lat: 37.7749, lng: -122.4194 }, // San Francisco
          deviceInfo: { model: 'iPhone 13', os: 'iOS 15.4' },
          client: useClient,
          userId: targetUserId,
          userName: userData.name || userData.displayName || 'Unknown User'
        },
        {
          timestamp: firebase.firestore.Timestamp.fromDate(oneHourAgo),
          locationId: clientLocations.length > 1 ? clientLocations[1].id : 'sample-loc-2',
          locationName: clientLocations.length > 1 ? clientLocations[1].name : 'Sample Location 2',
          status: 'complete',
          deviceType: 'Android',
          coordinates: { lat: 37.7833, lng: -122.4167 }, // Close to San Francisco
          deviceInfo: { model: 'Samsung Galaxy S21', os: 'Android 12' },
          client: useClient,
          userId: targetUserId,
          userName: userData.name || userData.displayName || 'Unknown User'
        },
        // Unauthorized locations
        {
          timestamp: firebase.firestore.Timestamp.fromDate(twoDaysAgo),
          locationId: unauthorizedLocations[0]?.id || 'unauth-loc-1',
          locationName: unauthorizedLocations[0]?.name || 'Unauthorized Location 1',
          status: 'complete',
          deviceType: 'Web',
          coordinates: { lat: 37.7944, lng: -122.3937 }, // Close to San Francisco
          deviceInfo: { model: 'Chrome', os: 'Windows 11' },
          client: unauthorizedLocations[0]?.client || 'other-client',
          userId: targetUserId,
          userName: userData.name || userData.displayName || 'Unknown User'
        },
        // Add another unauthorized location if available
        ...(unauthorizedLocations.length > 1 ? [{
          timestamp: firebase.firestore.Timestamp.fromDate(threeDaysAgo),
          locationId: unauthorizedLocations[1]?.id,
          locationName: unauthorizedLocations[1]?.name,
          status: 'complete',
          deviceType: 'Mobile Web',
          coordinates: { lat: 37.8025, lng: -122.4051 }, // Different location
          deviceInfo: { model: 'Safari', os: 'iOS 16' },
          client: unauthorizedLocations[1]?.client,
          userId: targetUserId,
          userName: userData.name || userData.displayName || 'Unknown User'
        }] : [])
      ];
      
      // Add the sample data to Firestore
      const batch = db.batch();
      sampleData.forEach(data => {
        const newDocRef = checkinsRef.doc();
        batch.set(newDocRef, data);
      });
      
      await batch.commit();
      console.log("Sample checkin data created with the following details:");
      sampleData.forEach((data, index) => {
        console.log(`Sample #${index + 1}:`);
        console.log(`- Location: ${data.locationName} (${clientLocations.some(loc => loc.id === data.locationId) ? 'Authorized' : 'Unauthorized'})`);
        console.log(`- Device Type: ${data.deviceType}`);
        console.log(`- Coordinates: ${JSON.stringify(data.coordinates)}`);
      });
      
      // Refresh the checkins
      await fetchCheckins();
      
    } catch (error) {
      console.error("Error creating sample data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createTestCheckin = async () => {
    if (!client && !selectedUser) {
      alert('Please select either a client or a user before creating test data.');
      return;
    }

    if (!window.confirm('This will create a single test check-in with coordinates and device info. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      console.log("Creating test check-in with guaranteed coordinates and device info...");
      const db = firebase.firestore();
      
      // Get the target user - either the selected user or current user
      let targetUserId = selectedUser;
      
      if (!targetUserId) {
        // If filtering by client, use the current user
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) {
          console.error("No logged in user found");
          return;
        }
        targetUserId = currentUser.uid;
      }
      
      // Get the user document
      const userRef = db.collection('users').doc(targetUserId);
      const userDoc = await userRef.get();
      
      if (!userDoc.exists) {
        console.error("User document not found");
        return;
      }
      
      const userData = userDoc.data();
      
      // Determine which client to use
      const useClient = client || userData.client;
      
      // Get a location
      let locationName = "Test Location";
      let locationId = "test-location";
      
      if (props.locations && props.locations.length > 0) {
        const clientLocations = props.locations.filter(loc => loc.client === useClient);
        if (clientLocations.length > 0) {
          locationName = clientLocations[0].name;
          locationId = clientLocations[0].id;
        }
      }
      
      // Create test data with explicit values
      const testData = {
        timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
        locationId: locationId,
        locationName: locationName,
        status: 'active',
        // Explicitly set device info in multiple formats to ensure it's captured
        deviceType: 'Test Device',
        device_type: 'Test Device', // alternative field name
        // Explicitly set coordinates in multiple formats to ensure they're captured
        coordinates: { lat: 37.7749, lng: -122.4194 }, // San Francisco
        location_coords: { lat: 37.7749, lng: -122.4194 }, // alternative field name
        gps: { lat: 37.7749, lng: -122.4194 }, // another alternative field name
        deviceInfo: { 
          model: 'Test Model', 
          os: 'Test OS',
          type: 'Test Device Type'
        },
        device_info: { // alternative field name
          model: 'Test Model', 
          os: 'Test OS'
        },
        client: useClient,
        userId: targetUserId,
        userName: userData.name || userData.displayName || 'Unknown User'
      };
      
      // Add directly to the main checkins collection first
      await db.collection('checkins').add(testData);
      
      // Also add to the user's checkins subcollection
      await userRef.collection('checkins').add(testData);
      
      console.log("Test check-in created with the following details:");
      console.log(`- Device Type: ${testData.deviceType}`);
      console.log(`- Coordinates: ${JSON.stringify(testData.coordinates)}`);
      
      // Refresh the checkins
      await fetchCheckins();
      
    } catch (error) {
      console.error("Error creating test data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Add new function to create multiple test formats
  const createMultiFormatCheckin = async () => {
    if (!client && !selectedUser) {
      alert('Please select either a client or a user before creating test data.');
      return;
    }

    if (!window.confirm('This will create multiple check-ins with different coordinate formats for testing. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      console.log("Creating test check-ins with different coordinate formats...");
      const db = firebase.firestore();
      
      // Get the target user - either the selected user or current user
      let targetUserId = selectedUser;
      
      if (!targetUserId) {
        // If filtering by client, use the current user
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) {
          console.error("No logged in user found");
          return;
        }
        targetUserId = currentUser.uid;
      }
      
      // Get the user document
      const userRef = db.collection('users').doc(targetUserId);
      const userDoc = await userRef.get();
      
      if (!userDoc.exists) {
        console.error("User document not found");
        return;
      }
      
      const userData = userDoc.data();
      
      // Determine which client to use
      const useClient = client || userData.client;
      
      // Get a location
      let locationName = "Test Location";
      let locationId = "test-location";
      
      if (props.locations && props.locations.length > 0) {
        const clientLocations = props.locations.filter(loc => loc.client === useClient);
        if (clientLocations.length > 0) {
          locationName = clientLocations[0].name;
          locationId = clientLocations[0].id;
        }
      }
      
      // Create multiple test data formats
      const testDataFormats = [
        // Format 1: Standard coordinates object
        {
          name: "Format 1: Standard coordinates",
          data: {
            timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
            locationId: locationId,
            locationName: locationName,
            status: 'active',
            deviceType: 'Format 1',
            coordinates: { lat: 37.7749, lng: -122.4194 },
            client: useClient,
            userId: targetUserId,
            userName: userData.name || userData.displayName || 'Unknown User'
          }
        },
        // Format 2: GPS field with latitude/longitude
        {
          name: "Format 2: GPS field",
          data: {
            timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
            locationId: locationId,
            locationName: locationName,
            status: 'active',
            deviceType: 'Format 2',
            gps: { 
              latitude: 37.7749, 
              longitude: -122.4194,
              accuracy: 10,
              altitude: 100,
              heading: 90,
              speed: 0
            },
            client: useClient,
            userId: targetUserId,
            userName: userData.name || userData.displayName || 'Unknown User'
          }
        },
        // Format 3: Direct latitude/longitude fields
        {
          name: "Format 3: Direct lat/lng",
          data: {
            timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
            locationId: locationId,
            locationName: locationName,
            status: 'active',
            deviceType: 'Format 3',
            latitude: 37.7749,
            longitude: -122.4194,
            client: useClient,
            userId: targetUserId,
            userName: userData.name || userData.displayName || 'Unknown User'
          }
        },
        // Format 4: Nested location object with coordinates
        {
          name: "Format 4: Nested location object",
          data: {
            timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
            locationId: locationId,
            locationName: locationName,
            status: 'active',
            deviceType: 'Format 4',
            location: {
              coordinates: { lat: 37.7749, lng: -122.4194 }
            },
            client: useClient,
            userId: targetUserId,
            userName: userData.name || userData.displayName || 'Unknown User'
          }
        },
        // Format 5: Position object (browser geolocation format)
        {
          name: "Format 5: Position object",
          data: {
            timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
            locationId: locationId,
            locationName: locationName,
            status: 'active',
            deviceType: 'Format 5',
            position: {
              coords: {
                latitude: 37.7749,
                longitude: -122.4194,
                accuracy: 10,
                altitude: 100,
                heading: 90,
                speed: 0
              },
              timestamp: Date.now()
            },
            client: useClient,
            userId: targetUserId,
            userName: userData.name || userData.displayName || 'Unknown User'
          }
        },
        // Format 6: location_coords field
        {
          name: "Format 6: location_coords field",
          data: {
            timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
            locationId: locationId,
            locationName: locationName,
            status: 'active',
            deviceType: 'Format 6',
            location_coords: { lat: 37.7749, lng: -122.4194 },
            client: useClient,
            userId: targetUserId,
            userName: userData.name || userData.displayName || 'Unknown User'
          }
        }
      ];
      
      // Add test data to Firestore
      const checkinsRef = userRef.collection('checkins');
      const batch = db.batch();
      
      testDataFormats.forEach(format => {
        console.log(`Creating ${format.name}`);
        const newDocRef = checkinsRef.doc();
        batch.set(newDocRef, format.data);
      });
      
      await batch.commit();
      console.log("Test check-ins created with multiple formats");
      
      // Refresh the checkins
      await fetchCheckins();
      
    } catch (error) {
      console.error("Error creating test data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateFileData = () => {
    let checkinsTemp = [...filterCheckins()];
    let csvDataArray = checkinsTemp.map(checkin => ([
      checkin.userName || 'Unknown User',
      moment(checkin.created_at).format('MM/DD/Y h:mm a'),
      checkin.location_name || 'Unknown Location',
      checkin.status || 'Unknown',
      checkin.deviceType || 'Unknown Device',
      checkin.coordinates ? 
        `${parseFloat(checkin.coordinates.lat).toFixed(5)}, ${parseFloat(checkin.coordinates.lng || checkin.coordinates.lon).toFixed(5)}` :
        'N/A',
      checkin.deviceInfo?.model || 'N/A',
      checkin.deviceInfo?.os || 'N/A'
    ]));

    // Update the header to match the new data structure
    const csvHeader = [
      'Name',
      'Date/Time',
      'Location',
      'Status',
      'Device Type',
      'Coordinates',
      'Device Model',
      'Operating System'
    ];

    return [csvHeader, ...csvDataArray];
  };

  const generateList = () => {
    if (endablePdfMode) {
      return filterCheckins();
    } else {
      return filterCheckins().slice((page - 1) * pageSize, ((page - 1) * pageSize) + pageSize);
    }
  };

  const getTargetElement = () => document.getElementById('content-id');

  const generateFilePdf = () => {
    const options = {
      method: 'save',
      resolution: Resolution.NORMAL,
      page: {
        margin: Margin.SMALL,
        format: 'A4',
        orientation: 'landscape', // Changed to landscape for better table fit
      },
      canvas: {
        mimeType: 'image/jpeg',
        qualityRatio: 1
      },
      overrides: {
        pdf: {
          compress: true
        },
        canvas: {
          useCORS: true
        }
      },
      filename: 'enforceplus_checkins.pdf'
    };

    setEndablePdfMode(true);
    setTimeout(() => {
      generatePDF(getTargetElement, options);
      setTimeout(() => {
        setEndablePdfMode(false);
      }, 1000);
    }, 1000);
  };

  // Add effect to handle impersonation - set client to impersonated user's client
  useEffect(() => {
    // Debug impersonation props
    console.log("Impersonation props check:", {
      isImpersonating: props.impersonated,
      impersonatedUser: props.impersonatedUser,
      clientFromProps: props.impersonatedUser?.client,
      currentClient: client,
      allowCheckIn
    });
    
    // Check if user is impersonating
    if (props.impersonated && props.impersonatedUser) {
      console.log("Impersonation detected, setting client filter to impersonated user's client");
      
      // Find the impersonated user's client
      const impersonatedUserClient = props.impersonatedUser.client;
      
      if (impersonatedUserClient) {
        console.log(`Setting client to: ${impersonatedUserClient}`);
        
        // Set client to impersonated user's client with slight delay to ensure render cycle completes
        setTimeout(() => {
          setClient(impersonatedUserClient);
          // Reset user filter to show all users for this client
          setSelectedUser('');
          // Ensure filter type is set to client
          setFilterType('client');
          
          // Check if check-ins are disabled and show modal
          const clientData = props.clients?.find(c => c.id === impersonatedUserClient);
          if (clientData && !clientData.allow_check_in) {
            console.log("Check-ins are disabled, showing feature modal");
            setShowFeatureModal(true);
          }
          
          // Trigger fetch after setting client
          setTimeout(() => fetchCheckins(), 100);
        }, 100);
      } else {
        console.warn("Impersonated user doesn't have a client assigned");
      }
    }
  }, [props.impersonated, props.impersonatedUser, props.clients]);

  // Also modify the initial useEffect to ensure it runs after props are available
  useEffect(() => {
    document.title = 'Checkins: EnforcePlus admin';
    
    if (!accessCheck(props.user, 'activities')) {
      window.location.href = '/';
      return;
    }

    // Determine which client to use
    let clientToUse;
    
    if (props.impersonated && props.impersonatedUser?.client) {
      // If impersonating, use impersonated user's client
      clientToUse = props.impersonatedUser.client;
    } else if (!props.user.super_user && props.user.client) {
      // If regular user (not super user), use their client
      clientToUse = props.user.client;
    }

    if (clientToUse) {
      console.log(`Setting initial client to: ${clientToUse}`);
      setClient(clientToUse);
      setFilterType('client');

      // Check if check-ins are disabled for this client
      const clientData = props.clients?.find(c => c.id === clientToUse);
      if (clientData) {
        const checkInsEnabled = !!clientData.allow_check_in;
        setAllowCheckIn(checkInsEnabled);
        
        // Show modal if check-ins are disabled
        if (!checkInsEnabled) {
          console.log("Check-ins are disabled, showing feature modal");
          setShowFeatureModal(true);
        }
      }
    }
  }, [props.user, props.impersonated, props.impersonatedUser, props.clients]);

  // Add effect to monitor client changes and check-in status
  useEffect(() => {
    if (client && props.clients) {
      const clientData = props.clients.find(c => c.id === client);
      if (clientData) {
        const checkInsEnabled = !!clientData.allow_check_in;
        setAllowCheckIn(checkInsEnabled);
        
        // Show modal if check-ins are disabled
        if (!checkInsEnabled) {
          console.log("Check-ins are disabled, showing feature modal");
          setShowFeatureModal(true);
        }
      }
    }
  }, [client, props.clients]);

  useEffect(() => {
    // Only fetch if a client or user is selected
    if (client || selectedUser) {
      fetchCheckins();
    }
  }, [client, selectedUser, startDate, endDate]);

  // Add an effect to reset location when client changes
  useEffect(() => {
    // Reset location when client changes
    setLocation('all');
  }, [client]);

  useEffect(() => {
    // If user is selected, clear client
    if (selectedUser) {
      setClient('');
    }
  }, [selectedUser]);

  // Update effect to properly initialize heat map mode when changing views
  useEffect(() => {
    // Check if Google Maps API is loaded when view mode changes to map
    if (viewMode === 'map' && (!window.google || !window.google.maps)) {
      console.warn("Google Maps API not loaded properly. Some map features may not work.");
    }
    
    // Reset any open info windows when changing view modes
    setSelectedMarker(null);
    setHoveredLocation(null);
    setProximityLine(null);
    
    // Always ensure heatMapEnabled is properly set when changing views
    if (viewMode === 'map') {
      // Ensure heat map is disabled by default when switching to map view
      console.log("Switching to map view, initializing with heat map disabled");
      setHeatMapEnabled(false);
    } else if (viewMode !== 'map') {
      // Reset heat map mode when switching away from map view
      setHeatMapEnabled(false);
    }
  }, [viewMode]);

  // Add debugging for heatMapEnabled toggle
  useEffect(() => {
    console.log(`Heat map mode is now ${heatMapEnabled ? 'enabled' : 'disabled'}`);
  }, [heatMapEnabled]);

  // Check when Maps API is fully loaded with visualization library
  useEffect(() => {
    const checkMapsLoaded = () => {
      if (isGoogleMapsLoaded()) {
        setMapsLibraryLoaded(true);
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (!checkMapsLoaded()) {
      // If not loaded, set up a polling mechanism to check every 500ms
      const intervalId = setInterval(() => {
        if (checkMapsLoaded()) {
          clearInterval(intervalId);
        }
      }, 500);
      
      // Clean up interval
      return () => clearInterval(intervalId);
    }
  }, []);
  
  // Use another effect to force re-render when switching to heatmap view
  useEffect(() => {
    if (viewMode === 'heatmap') {
      // If maps API isn't loaded yet, wait for it
      if (!mapsLibraryLoaded) {
        const checkInterval = setInterval(() => {
          if (isGoogleMapsLoaded()) {
            clearInterval(checkInterval);
            // Force re-render 
            setMapsLibraryLoaded(true);
          }
        }, 300);
        
        return () => clearInterval(checkInterval);
      }
    }
  }, [viewMode, mapsLibraryLoaded]);

  const filterCheckins = () => {
    // If no client or user is selected, return empty array
    if ((!client || client === '') && !selectedUser) {
      return [];
    }
    
    let sortedCheckins = [...checkins];
    
    // If specific user is selected, filter by that user
    if (selectedUser) {
      sortedCheckins = sortedCheckins.filter(checkin => checkin.user === selectedUser);
    }
    
    // Text search
    if (search !== '') {
      sortedCheckins = sortedCheckins.filter(checkin => (
        checkin.userName?.toLowerCase().includes(search.toLowerCase()) ||
        checkin.location_name?.toLowerCase().includes(search.toLowerCase()) ||
        checkin.deviceType?.toLowerCase().includes(search.toLowerCase())
      ));
    }
    
    // Status filter
    if (status !== 'all') {
      sortedCheckins = sortedCheckins.filter(checkin => checkin.status === status);
    }
    
    // Client filter is now implicit - we only fetch data for the selected client or user's client
    
    // Location filter
    if (location !== 'all') {
      sortedCheckins = sortedCheckins.filter(checkin => checkin.location === location);
    }
    
    // Device type filter
    if (deviceType !== 'all') {
      sortedCheckins = sortedCheckins.filter(checkin => checkin.deviceType === deviceType);
    }
    
    // Sorting
    if (sortBy === 'dateDesc') {
      sortedCheckins = sortedCheckins.sort((a, b) => 
        a.created_at && b.created_at && moment(a.created_at).isBefore(moment(b.created_at)) ? 1 : -1
      );
    }
    if (sortBy === 'dateAsc') {
      sortedCheckins = sortedCheckins.sort((a, b) => 
        a.created_at && b.created_at && moment(a.created_at).isBefore(moment(b.created_at)) ? -1 : 1
      );
    }
    if (sortBy === 'userAsc') {
      sortedCheckins = sortedCheckins.sort((a, b) => 
        a.userName?.localeCompare(b.userName)
      );
    }
    if (sortBy === 'userDesc') {
      sortedCheckins = sortedCheckins.sort((a, b) => 
        b.userName?.localeCompare(a.userName)
      );
    }

    return sortedCheckins.slice();
  };

  // Calculate weight for heat map points based on recency
  const calculateHeatMapWeight = (checkin) => {
    if (!checkin) return 1;
    
    const checkinDate = checkin.timestamp ? 
      (checkin.timestamp.toDate ? checkin.timestamp.toDate() : new Date(checkin.timestamp)) : 
      checkin.created_at || new Date();
      
    const now = new Date();
    const diffTime = Math.abs(now - checkinDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Weight factors
    // 1. Recency factor - newer check-ins have higher weight
    let recencyFactor;
    if (diffDays < 1) {
      recencyFactor = 3; // Today
    } else if (diffDays < 3) {
      recencyFactor = 2.5; // Last 3 days
    } else if (diffDays < 7) {
      recencyFactor = 2; // Last week
    } else if (diffDays < 30) {
      recencyFactor = 1.5; // Last month
    } else {
      recencyFactor = 1; // Older
    }
    
    // 2. User activity factor - if user has multiple check-ins, increase weight
    // This will make frequent users' check-ins appear more prominently
    const userCheckinsCount = filterCheckins().filter(c => c.userId === checkin.userId).length || 1;
    const userActivityFactor = Math.min(Math.log(userCheckinsCount + 1) / Math.log(4), 0.3) + 1;
    
    // 3. Time of day factor - optional: can make certain times of day more prominent
    const hour = checkinDate.getHours();
    const timeOfDayFactor = 
      (hour >= 9 && hour <= 17) ? 1.2 : // Business hours
      (hour >= 6 && hour <= 23) ? 1 : // Normal active hours
      0.8; // Late night
    
    return recencyFactor * userActivityFactor * timeOfDayFactor;
  };

  // Add a function to find the nearest authorized location to a check-in
  const findNearestLocation = (checkinCoords) => {
    if (!checkinCoords || !checkinCoords.lat || !checkinCoords.lng) {
      return null;
    }
    
    // Filter locations with valid coordinates
    const locationsWithCoords = clientLocations.filter(loc => {
      const lat = parseFloat(loc.lat || loc.latitude || (loc.coordinates ? loc.coordinates.lat : 0));
      const lng = parseFloat(loc.lng || loc.longitude || (loc.coordinates ? loc.coordinates.lng : 0));
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
    });
    
    if (locationsWithCoords.length === 0) {
      return null;
    }
    
    // Find closest location
    let nearestLocation = null;
    let minDistance = Infinity;
    
    locationsWithCoords.forEach(loc => {
      const lat = parseFloat(loc.lat || loc.latitude || (loc.coordinates ? loc.coordinates.lat : 0));
      const lng = parseFloat(loc.lng || loc.longitude || (loc.coordinates ? loc.coordinates.lng : 0));
      
      const distance = calculateDistance(
        checkinCoords.lat, 
        checkinCoords.lng, 
        lat, 
        lng
      );
      
      if (distance !== null && distance < minDistance) {
        minDistance = distance;
        nearestLocation = {
          ...loc,
          distance
        };
      }
    });
    
    return nearestLocation;
  };

  // Update the click handler for check-in markers to also set a proximity line
  const handleCheckinClick = (checkin) => {
    if (!checkin || !checkin.coordinates) {
      console.warn('Invalid check-in data for proximity calculation');
      setSelectedMarker(null);
      setProximityLine(null);
      return;
    }
    
    setSelectedMarker({...checkin, coordinates: checkin.coordinates});
    
    // Find nearest location and draw line
    const nearest = findNearestLocation(checkin.coordinates);
    if (nearest) {
      const nearestCoords = {
        lat: parseFloat(nearest.lat || nearest.latitude || (nearest.coordinates ? nearest.coordinates.lat : 0)),
        lng: parseFloat(nearest.lng || nearest.longitude || (nearest.coordinates ? nearest.coordinates.lng : 0))
      };
      
      if (!isNaN(nearestCoords.lat) && !isNaN(nearestCoords.lng) && 
          nearestCoords.lat !== 0 && nearestCoords.lng !== 0) {
        setProximityLine({
          path: [checkin.coordinates, nearestCoords],
          distance: nearest.distance,
          locationName: nearest.name || 'Location'
        });
      } else {
        setProximityLine(null);
      }
    } else {
      setProximityLine(null);
    }
  };
  
  // Reset proximity line when closing InfoWindow
  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
    setProximityLine(null);
  };

  // Enhanced hover information for authorized locations
  const getLocationAddressDisplay = (location) => {
    if (!location) return 'No address available';
    
    let addressText = 'No address available';
    
    // Try to get address from various possible fields
    if (location.address) {
      addressText = location.address;
    } else if (location.street) {
      addressText = [
        location.street,
        location.city,
        location.state,
        location.zip
      ].filter(Boolean).join(', ');
    } else if (location.formatted_address) {
      addressText = location.formatted_address;
    }
    
    return addressText;
  };

  // Add effect to determine user role
  useEffect(() => {
    if (props.user) {
      // Check if the user is a super user with access to all clients
      const superUser = props.user.super_user || props.user.superUser || props.user.role === 'super';
      setIsSuperUser(superUser);
      
      // Check if user is an admin (has a client but not super user)
      const admin = !superUser && (props.user.client || props.user.role === 'admin');
      setIsAdmin(admin);
      
      // Get the user's client ID
      let clientId = props.user.client || '';
      
      // Define impersonation variables
      let impersonatedSuperUser = false;
      let impersonatedAdmin = false;
      
      // If impersonating, use the impersonated user's role and client
      if (props.impersonated && props.impersonatedUser) {
        impersonatedSuperUser = props.impersonatedUser.super_user || 
                              props.impersonatedUser.superUser || 
                              props.impersonatedUser.role === 'super';
        impersonatedAdmin = !impersonatedSuperUser && 
                           (props.impersonatedUser.client || 
                            props.impersonatedUser.role === 'admin');
        
        setIsSuperUser(impersonatedSuperUser);
        setIsAdmin(impersonatedAdmin);
        
        if (impersonatedAdmin && props.impersonatedUser.client) {
          clientId = props.impersonatedUser.client;
        }
      }
      
      console.log(`User role: ${isSuperUser ? 'Super User' : (isAdmin ? 'Admin' : 'Other')}, Client ID: ${clientId}`);
      setUserClientId(clientId);
      
      // For admins, automatically set their client
      if ((admin || (props.impersonated && props.impersonatedUser && !impersonatedSuperUser)) && clientId) {
        setClient(clientId);
        setFilterType('client');
      }
    }
  }, [props.user, props.impersonated, props.impersonatedUser]);
  
  // Initiate data fetch for admins once their client is set
  useEffect(() => {
    // Auto fetch when client is set for admin users 
    if (client && ((isAdmin && client === userClientId) || 
        (props.impersonated && props.impersonatedUser && !isSuperUser))) {
      fetchCheckins();
    }
  }, [client, isAdmin, userClientId]);

  // Update useEffect to handle PDF styles
  useEffect(() => {
    if (endablePdfMode) {
      setPdfTableStyles({
        fontSize: '10px',
        padding: '4px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      });
    } else {
      setPdfTableStyles({});
    }
  }, [endablePdfMode]);

  // Add effect to initialize allowCheckIn state when client changes
  useEffect(() => {
    if (client && props.clients) {
      const clientData = props.clients.find(c => c.id === client);
      if (clientData) {
        setAllowCheckIn(!!clientData.allow_check_in);
        setSelectedClient(clientData);
      }
    }
  }, [client, props.clients]);

  const handleCheckinToggle = () => {
    if (!allowCheckIn) {
      setShowFeatureModal(true);
    } else {
      setShowConfirmDialog(true);
    }
  };

  const handleEnableCheckins = async () => {
    if (!client) return;
    
    try {
      // Get the client data
      const clientData = props.clients?.find(c => c.id === client);
      if (!clientData) {
        console.error("Client data not found");
        return;
      }

      // Update client with new check-in status in Firestore
      await firebase.firestore()
        .collection('clients')
        .doc(client)
        .update({
          allow_check_in: true,
          updated_at: firebase.firestore.FieldValue.serverTimestamp()
        });
      
      // Update local state
      setAllowCheckIn(true);
      setShowFeatureModal(false);
      
      // Refresh clients data if the function exists
      if (typeof props.getLiveClients === 'function') {
        props.getLiveClients();
      }

      console.log("Successfully enabled check-ins for client:", client);
    } catch (error) {
      console.error("Error enabling check-ins:", error);
      alert("Failed to enable check-ins. Please try again.");
    }
  };

  // Update the check-in status update function to use Firestore directly
  const updateCheckInStatus = async (newStatus) => {
    if (!client) return;
    
    try {
      // Update client with new check-in status
      await firebase.firestore()
        .collection('clients')
        .doc(client)
        .update({
          allow_check_in: newStatus,
          updated_at: firebase.firestore.FieldValue.serverTimestamp()
        });
      
      // Update local state
      setAllowCheckIn(newStatus);
      setShowConfirmDialog(false);
      
      // Refresh clients data if the function exists
      if (typeof props.getLiveClients === 'function') {
        props.getLiveClients();
      }

      console.log(`Successfully ${newStatus ? 'enabled' : 'disabled'} check-ins for client:`, client);
    } catch (error) {
      console.error("Error updating check-in status:", error);
      alert("Failed to update check-in settings. Please try again.");
    }
  };

  // Add helper function to generate Google Maps URL
  const getGoogleMapsUrl = (coordinates) => {
    if (!coordinates || !coordinates.lat || (!coordinates.lng && !coordinates.lon)) return null;
    const lng = coordinates.lng || coordinates.lon;
    return `https://www.google.com/maps?q=${coordinates.lat},${lng}`;
  };

  // Add state to track map initialization
  const [mapKey, setMapKey] = useState(0);

  // Add state for map initialization
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);

  // Update the view mode handler
  const handleViewModeChange = (newMode) => {
    setViewMode(newMode);
    if (newMode === 'map') {
      // Reset map-related states
      setSelectedMarker(null);
      setHoveredLocation(null);
      setProximityLine(null);
      setHeatMapEnabled(false);
      setIsMapInitialized(false); // Force reinitialization
      setMapKey(prevKey => prevKey + 1);
    }
  };

  // Add cleanup effect
  useEffect(() => {
    return () => {
      if (mapInstance) {
        // Clean up map instance when component unmounts
        setMapInstance(null);
      }
    };
  }, []);

  // Add effect to handle map initialization
  useEffect(() => {
    if (viewMode === 'map' && mapInstance && window.google) {
      // Force a resize and recenter when map is displayed
      window.google.maps.event.trigger(mapInstance, 'resize');
      if (mapCenter) {
        mapInstance.setCenter(mapCenter);
      }
    }
  }, [viewMode, mapInstance, mapCenter]);

  // Add state for drawing functionality
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const [drawingMode, setDrawingMode] = useState(null);
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadius, setCircleRadius] = useState(null);

  // Update the handleMapClick function to use window.google
  const handleMapClick = useCallback((event) => {
    if (!isDrawingEnabled) return;

    const clickedLocation = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };

    if (drawingMode === 'polygon') {
      setPolygonPoints(prevPoints => [...prevPoints, clickedLocation]);
    } else if (drawingMode === 'circle') {
      if (!circleCenter) {
        setCircleCenter(clickedLocation);
      } else if (!circleRadius) {
        const radius = window.google.maps.geometry.spherical.computeDistanceBetween(
          new window.google.maps.LatLng(circleCenter.lat, circleCenter.lng),
          new window.google.maps.LatLng(clickedLocation.lat, clickedLocation.lng)
        );
        setCircleRadius(radius);
        setDrawingMode(null);
        setIsDrawingEnabled(false);
      }
    }
  }, [isDrawingEnabled, drawingMode, circleCenter]);

  return (
    <Container fluid className="main-content-container px-4">
      <Row noGutters className="page-header py-4">
        <Col xs="12" sm="8" style={{ position: 'relative', zIndex: 0 }}>
          <PageTitle
            title="Check-ins"
            className="text-sm-left"
          />
        </Col>
        <Col xs="12" sm="4" className="d-flex align-items-center justify-content-end" style={{ position: 'relative', zIndex: 1 }}>
          <Form style={{ marginBottom: 0 }}>
            <Row form className="align-items-center">
              <Col md="12" className="form-group mb-0 d-flex align-items-center">
                <label htmlFor="feAllowCheckIn" className="mr-2 mb-0" style={{ 
                  fontSize: '14px',
                  color: client ? '#3D5170' : '#8FA4B9',
                  whiteSpace: 'nowrap'
                }}>
                  Enable Check-ins
                </label>
                <FormCheckbox
                  id="feAllowCheckIn"
                  toggle
                  checked={allowCheckIn}
                  onChange={() => window.handleCheckInToggle()}
                  disabled={!client}
                  className="d-inline-block"
                />
                <Button
                  theme="light"
                  style={{
                    padding: '4px 8px',
                    marginLeft: '8px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    boxShadow: 'none'
                  }}
                  onClick={() => setShowInfoModal(true)}
                >
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    style={{ 
                      color: '#21AA73',
                      fontSize: '16px'
                    }}
                  />
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      {/* Add confirmation dialog */}
      <Modal
        open={showConfirmDialog}
        toggle={() => setShowConfirmDialog(false)}
      >
        <ModalHeader>Disable Check-ins?</ModalHeader>
        <ModalBody>
          <p>Are you sure you want to disable check-ins for this client? This will prevent users from checking in at locations.</p>
          <div className="text-right mt-4">
            <Button
              theme="secondary"
              onClick={() => setShowConfirmDialog(false)}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              theme="danger"
              onClick={async () => {
                try {
                  const clientData = props.clients.find(c => c.id === client);
                  if (clientData) {
                    await firebase.firestore()
                      .collection('clients')
                      .doc(client)
                      .update({
                        allow_check_in: false,
                        updated_at: firebase.firestore.FieldValue.serverTimestamp()
                      });
                    
                    setAllowCheckIn(false);
                    setShowConfirmDialog(false);
                    
                    // Refresh clients data if the function exists
                    if (typeof props.getLiveClients === 'function') {
                      props.getLiveClients();
                    }
                  }
                } catch (error) {
                  console.error("Error updating client:", error);
                  alert("Failed to update check-in settings. Please try again.");
                }
              }}
            >
              Disable Check-ins
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* Update the handleCheckInToggle function */}
      {(() => {
        window.handleCheckInToggle = async () => {
          if (!client) return;
          
          if (allowCheckIn) {
            setShowConfirmDialog(true);
          } else {
            try {
              await firebase.firestore()
                .collection('clients')
                .doc(client)
                .update({
                  allow_check_in: true,
                  updated_at: firebase.firestore.FieldValue.serverTimestamp()
                });
              
              setAllowCheckIn(true);
              
              // Refresh clients data if the function exists
              if (typeof props.getLiveClients === 'function') {
                props.getLiveClients();
              }
            } catch (error) {
              console.error("Error updating client:", error);
              alert("Failed to update check-in settings. Please try again.");
            }
          }
        };
      })()}

      <CheckinFeatureModal 
        isOpen={showFeatureModal}
        toggle={() => setShowFeatureModal(false)}
        onEnable={handleEnableCheckins}
      />

      <CheckinFeatureModal 
        isOpen={showInfoModal}
        toggle={() => setShowInfoModal(false)}
        infoMode={true}
      />

      {isSuperUser && (!client || client === '') && !selectedUser ? (
        <Card small className="mb-4" style={{
          borderRadius: '6px',
          boxShadow: '0 1px 10px rgba(0,0,0,0.05)',
          border: '1px solid #eaedf3'
        }}>
          {/* ... existing card content ... */}
        </Card>
      ) : loading ? (
        <Row>
          <Col className={'text-center'}>
            <ActivityIndicator />
          </Col>
        </Row>
      ) : (
        <>
          <Card small className="mb-4" style={{
            borderRadius: '6px',
            boxShadow: '0 1px 10px rgba(0,0,0,0.05)',
            border: '1px solid #eaedf3',
            margin: '0'
          }}>
            <CardHeader className="border-bottom">
              <Row>
                <Col xs={6} className="d-flex align-items-center">
                  <h6 className="m-0 font-weight-bold d-flex align-items-center">
                    <i className="material-icons mr-2" style={{color: '#21AA73', opacity: 0.8}}>location_on</i>
                    ({filterCheckins().length})
                  </h6>
                  <ButtonGroup className="ml-3">
                    <Button 
                      theme={viewMode === 'table' ? 'primary' : 'white'} 
                      onClick={() => handleViewModeChange('table')}
                    >
                      <i className="material-icons">view_list</i> Table View
                    </Button>
                    <Button 
                      theme={viewMode === 'map' ? 'primary' : 'white'} 
                      onClick={() => handleViewModeChange('map')}
                    >
                      <i className="material-icons">map</i> Map View
                    </Button>
                  </ButtonGroup>
                  
                  {/* Custom heat map toggle button */}
                  {viewMode === 'map' && (
                    <Button
                      theme={heatMapEnabled ? 'primary' : 'white'}
                      onClick={() => setHeatMapEnabled(!heatMapEnabled)}
                      className="ml-3"
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '0.375rem 0.75rem',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="material-icons mr-1" style={{ fontSize: '18px' }}>
                        whatshot
                      </i>
                      Heat Map
                    </Button>
                  )}
                </Col>
                <Col xs={6} className='text-right pl-0 d-flex align-items-center justify-content-end'>
                  <div className="position-relative flex-grow-1 mr-2">
                    <i className="material-icons position-absolute" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#66758F', fontSize: '20px' }}>search</i>
                    <FormInput 
                      type="text"
                      placeholder="Search user, location, or device"
                      value={search}
                      onChange={event => setSearch(event.target.value)}
                      style={{
                        height: '40px',
                        borderRadius: '8px',
                        paddingLeft: '35px',
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#F8FAFC',
                        fontSize: '14px',
                        width: '100%'
                      }}
                    />
                  </div>
                  <div style={{ minWidth: '150px' }}>
                    <DownloadButton
                      data={generateFileData()}
                      filename="enforceplus_checkins"
                      style={{
                        height: '40px',
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        backgroundColor: '#FFFFFF',
                        fontSize: '14px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 15px',
                        color: '#1E293B'
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className={`p-0 ${viewMode === 'map' ? 'p-3' : ''}`} id="content-id">
              {viewMode === 'table' ? (
                <table className="table mb-0">
                  <thead className="bg-none">
                    <tr>
                      <th scope="col" className="border-0" style={pdfTableStyles}>
                        <div onClick={() => setSortBy(sortBy !== 'userAsc' ? 'userAsc' : 'userDesc')} style={{cursor: 'pointer'}}>Name</div>
                      </th>
                      <th scope="col" className="border-0" style={pdfTableStyles}>
                        <div onClick={() => setSortBy(sortBy !== 'dateDesc' ? 'dateDesc' : 'dateAsc')} style={{cursor: 'pointer'}}>Date/Time</div>
                      </th>
                      <th scope="col" className="border-0" style={pdfTableStyles}>
                        <div>Location</div>
                      </th>
                      <th scope="col" className="border-0" style={pdfTableStyles}>
                        <div>Device Type</div>
                      </th>
                      <th scope="col" className="border-0" style={pdfTableStyles}>
                        <div>Status</div>
                      </th>
                      <th scope="col" className="border-0" style={pdfTableStyles}>
                        <div>Coordinates</div>
                      </th>
                      <th scope="col" className="border-0" style={pdfTableStyles}>
                        <div>Device Info</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkins.length > 0 ? (
                      generateList().map(checkin => {
                        const mapsUrl = getGoogleMapsUrl(checkin.coordinates);
                        return (
                          <tr key={checkin.id}>
                            <td style={pdfTableStyles}>
                              {checkin.userName || 'Unknown User'}
                            </td>
                            <td style={pdfTableStyles}>
                              {checkin.created_at ? moment(checkin.created_at).format('MM/DD/Y h:mm a') : '—'}
                            </td>
                            <td style={pdfTableStyles}>
                              {checkin.location_name || 'Unknown Location'}
                            </td>
                            <td style={pdfTableStyles}>
                              {checkin.deviceType || 'Unknown Device'}
                            </td>
                            <td style={pdfTableStyles}>
                              {checkin.status || 'Unknown'}
                            </td>
                            <td style={pdfTableStyles}>
                              {checkin.coordinates ? (
                                <a 
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="coordinates-link"
                                  title="Open in Google Maps"
                                  style={{
                                    color: '#21AA73',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    transition: 'all 0.2s ease',
                                    display: 'inline-block',
                                    backgroundColor: 'transparent',
                                    border: '1px solid transparent'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#F0FDF4';
                                    e.currentTarget.style.border = '1px solid #21AA73';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(33, 170, 115, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.border = '1px solid transparent';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                >
                                  {`${parseFloat(checkin.coordinates.lat).toFixed(5)}, ${parseFloat(checkin.coordinates.lng || checkin.coordinates.lon).toFixed(5)}`}
                                  <i className="material-icons" style={{ 
                                    fontSize: '16px', 
                                    marginLeft: '4px', 
                                    verticalAlign: 'middle',
                                    opacity: 0.8
                                  }}>
                                    open_in_new
                                  </i>
                                </a>
                              ) : 'N/A'}
                            </td>
                            <td style={pdfTableStyles}>
                              {checkin.deviceInfo ? 
                                `${checkin.deviceInfo.model || 'N/A'} (${checkin.deviceInfo.os || 'N/A'})` :
                                'N/A'
                              }
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center" style={pdfTableStyles}>
                          No checkins found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <LoadScript 
                  key={mapKey}
                  googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} 
                  loadingElement={<ActivityIndicator />}
                  libraries={["visualization"]}
                  onLoad={() => {
                    console.log("Google Maps API loaded successfully");
                    setMapsLibraryLoaded(true);
                    setIsMapInitialized(true);
                  }}
                  onError={(error) => console.error("Error loading Google Maps API:", error)}
                >
                  {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={mapCenter}
                      zoom={mapZoom}
                      options={{
                        zoomControl: true,
                        streetViewControl: true,
                        mapTypeControl: true,
                        fullscreenControl: true,
                        gestureHandling: 'cooperative',
                        mapTypeId: 'roadmap',
                        mapTypeControlOptions: {
                          position: window.google?.maps?.ControlPosition?.TOP_RIGHT || 'TOP_RIGHT'
                        }
                      }}
                      onLoad={(map) => {
                        console.log("Google Map instance loaded");
                        setMapInstance(map);
                        // Force a resize after a short delay to ensure proper rendering
                        setTimeout(() => {
                          if (map) {
                            window.google.maps.event.trigger(map, 'resize');
                            if (mapCenter) {
                              map.setCenter(mapCenter);
                            }
                          }
                        }, 100);
                      }}
                    >
                      {/* Debug when rendering map content */}
                      {console.log("Map component rendering, heatMapEnabled:", heatMapEnabled)}
                      
                      {/* Show real locations - TEMPORARY FIX: always show pins regardless of heat map toggle */}
                      {window.google && clientLocations.map(location => {
                        // Check if location has coordinates
                        if (location.coordinates || location.lat || location.lng || location.latitude || location.longitude) {
                          // Extract coordinates
                          const position = {
                            lat: parseFloat(location.lat || location.latitude || (location.coordinates ? location.coordinates.lat : 0)),
                            lng: parseFloat(location.lng || location.longitude || (location.coordinates ? location.coordinates.lng : 0))
                          };
                          
                          if (!isNaN(position.lat) && !isNaN(position.lng) && position.lat !== 0 && position.lng !== 0) {
                            return (
                              <Marker
                                key={`location-${location.id}`}
                                position={position}
                                icon={markerIcons.authorizedLocation}
                                onMouseOver={() => setHoveredLocation(location)}
                                onMouseOut={() => setHoveredLocation(null)}
                                zIndex={100} // Make location pins appear above check-ins
                              />
                            );
                          }
                        }
                        return null;
                      })}
                      
                      {/* Show hover information for authorized locations */}
                      {hoveredLocation && window.google && window.google.maps && (
                        <InfoWindow
                          position={{
                            lat: parseFloat(hoveredLocation.lat || hoveredLocation.latitude || (hoveredLocation.coordinates ? hoveredLocation.coordinates.lat : 0)),
                            lng: parseFloat(hoveredLocation.lng || hoveredLocation.longitude || (hoveredLocation.coordinates ? hoveredLocation.coordinates.lng : 0))
                          }}
                          onCloseClick={() => setHoveredLocation(null)}
                          options={{ pixelOffset: new window.google.maps.Size(0, -40) }}
                        >
                          <div style={{ padding: '8px', maxWidth: '250px' }}>
                            <h6 style={{ color: '#21AA73', marginBottom: '5px' }}>{hoveredLocation.name || 'Location'}</h6>
                            <p style={{ fontSize: '13px', margin: '0 0 3px 0' }}>
                              {getLocationAddressDisplay(hoveredLocation)}
                            </p>
                            {hoveredLocation && hoveredLocation.phone && (
                              <p style={{ fontSize: '12px', margin: 0, color: '#666' }}>
                                {hoveredLocation.phone}
                              </p>
                            )}
                          </div>
                        </InfoWindow>
                      )}
                      
                      {/* Show check-in markers - use visibility instead of conditional rendering */}
                      {window.google && filterCheckins().map(checkin => {
                        // Make sure coordinates are valid and in the correct format
                        if (checkin.coordinates && 
                            typeof checkin.coordinates === 'object' && 
                            checkin.coordinates.lat && 
                            (checkin.coordinates.lng || checkin.coordinates.lon)) {
                          
                          // Ensure we have a valid lat/lng object for the Google Maps marker
                          const position = {
                            lat: parseFloat(checkin.coordinates.lat),
                            lng: parseFloat(checkin.coordinates.lng || checkin.coordinates.lon)
                          };
                          
                          if (!isNaN(position.lat) && !isNaN(position.lng)) {
                            return (
                              <Marker
                                key={checkin.id}
                                position={position}
                                onClick={() => handleCheckinClick({...checkin, coordinates: position})}
                                icon={markerIcons.checkinPin}
                                visible={!heatMapEnabled} // Use visibility instead of conditional rendering
                              />
                            );
                          }
                        }
                        return null;
                      })}

                      {/* Draw proximity line between check-in and nearest location */}
                      {proximityLine && proximityLine.path && proximityLine.path.length === 2 && (
                        <Polyline
                          path={proximityLine.path}
                          options={{
                            strokeColor: proximityLine.distance < 100 
                              ? '#21AA73' // Green if within 100m
                              : proximityLine.distance < 500
                                ? '#F4BB44' // Yellow/orange if within 500m
                                : '#E6492E', // Red if farther away
                            strokeOpacity: heatMapEnabled ? 0 : 0.8, // Hide the line when heat map is enabled
                            strokeWeight: 2,
                            geodesic: true
                          }}
                        />
                      )}

                      {/* Heat map layer (conditionally rendered based on toggle) */}
                      {heatMapEnabled && (isGoogleMapsLoaded() || mapsLibraryLoaded) && window.google && (
                        <HeatmapLayer
                          data={filterCheckins()
                            .filter(checkin => 
                              checkin.coordinates && 
                              typeof checkin.coordinates === 'object' &&
                              checkin.coordinates.lat && 
                              (checkin.coordinates.lng || checkin.coordinates.lon))
                            .map(checkin => {
                              try {
                                return {
                                  location: new window.google.maps.LatLng(
                                    parseFloat(checkin.coordinates.lat),
                                    parseFloat(checkin.coordinates.lng || checkin.coordinates.lon)
                                  ),
                                  weight: calculateHeatMapWeight(checkin)
                                };
                              } catch (error) {
                                console.warn("Error creating heat map point:", error);
                                return null;
                              }
                            })
                            .filter(point => point !== null)}
                          options={{
                            radius: heatMapIntensity,
                            opacity: 0.8,
                            maxIntensity: 10,
                            dissipating: true,
                            gradient: [
                              'rgba(0, 255, 255, 0)',
                              'rgba(0, 255, 255, 1)',
                              'rgba(0, 191, 255, 1)',
                              'rgba(0, 127, 255, 1)',
                              'rgba(0, 63, 255, 1)',
                              'rgba(0, 0, 255, 1)',
                              'rgba(0, 0, 223, 1)',
                              'rgba(0, 0, 191, 1)',
                              'rgba(0, 0, 159, 1)',
                              'rgba(0, 0, 127, 1)',
                              'rgba(63, 0, 91, 1)',
                              'rgba(127, 0, 63, 1)',
                              'rgba(191, 0, 31, 1)',
                              'rgba(255, 0, 0, 1)'
                            ]
                          }}
                        />
                      )}
                      
                      {/* Show the info window only when heat map is disabled */}
                      {!heatMapEnabled && selectedMarker && window.google && window.google.maps && (
                        <InfoWindow
                          position={selectedMarker.coordinates}
                          onCloseClick={handleInfoWindowClose}
                        >
                          <div style={{ padding: '8px' }}>
                            <h6>{selectedMarker.userName || 'User'}</h6>
                            <p>
                              <strong>Date:</strong> {moment(selectedMarker.created_at).format('MM/DD/Y h:mm a')}<br />
                              <strong>Location:</strong> {selectedMarker.location_name || 'Unknown Location'}<br />
                              <strong>Device:</strong> {selectedMarker.deviceType || 'Unknown Device'}<br />
                              <strong>Status:</strong> {selectedMarker.status || 'Unknown'}<br />
                              
                              {/* Show nearest location information */}
                              {(() => {
                                const nearest = findNearestLocation(selectedMarker.coordinates);
                                if (nearest) {
                                  const proximityColor = nearest.distance < 100 
                                    ? '#21AA73' // Green if within 100m
                                    : nearest.distance < 500
                                      ? '#F4BB44' // Yellow/orange if within 500m
                                      : '#E6492E'; // Red if farther away
                                      
                                  return (
                                    <>
                                      <strong>Nearest Location:</strong> {nearest.name || 'Location'}<br />
                                      <strong>Distance:</strong> <span style={{ color: proximityColor }}>
                                        {formatDistance(nearest.distance)}
                                      </span>
                                    </>
                                  );
                                }
                                return null;
                              })()}
                            </p>
                          </div>
                        </InfoWindow>
                      )}

                      {/* Add appropriate controls based on mode */}
                      {viewMode === 'map' && (
                        <div 
                          style={{
                            position: 'absolute',
                            bottom: '20px',
                            left: '10px',
                            backgroundColor: 'white',
                            padding: '10px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            zIndex: 1000
                          }}
                        >
                          {heatMapEnabled ? (
                            // Heat map controls
                            <>
                              <div style={{ marginBottom: '5px' }}><strong>Heat Map Intensity:</strong></div>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: '5px', fontSize: '12px' }}>Low</span>
                                <input 
                                  type="range" 
                                  min="5" 
                                  max="50" 
                                  value={heatMapIntensity} 
                                  onChange={(e) => setHeatMapIntensity(parseInt(e.target.value))} 
                                  style={{ width: '150px' }}
                                />
                                <span style={{ marginLeft: '5px', fontSize: '12px' }}>High</span>
                              </div>
                              
                              {/* Heat Map Color Legend */}
                              <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '5px' }}>
                                <div style={{ fontSize: '12px', marginBottom: '3px' }}><strong>Activity Level:</strong></div>
                                <div style={{ 
                                  display: 'flex', 
                                  height: '10px', 
                                  width: '100%', 
                                  marginBottom: '2px',
                                  background: 'linear-gradient(to right, rgba(0, 255, 255, 1), rgba(0, 127, 255, 1), rgba(0, 0, 255, 1), rgba(127, 0, 63, 1), rgba(255, 0, 0, 1))'
                                }}></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                                  <span>Low</span>
                                  <span>Medium</span>
                                  <span>High</span>
                                </div>
                                <div style={{ fontSize: '10px', marginTop: '3px' }}>
                                  <strong>Legend:</strong>
                                  <div>• Blue: Fewer check-ins</div>
                                  <div>• Red: More check-ins</div>
                                  <div>• Brighter areas: Recent activity</div>
                                </div>
                              </div>
                              
                              <div style={{ fontSize: '10px', marginTop: '5px', color: '#666' }}>
                                {filterCheckins().filter(c => c.coordinates).length} check-ins displayed
                              </div>
                            </>
                          ) : (
                            // Map legend for pins
                            <>
                              <div style={{ marginBottom: '5px' }}><strong>Map Legend:</strong></div>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                <svg height="24" width="24">
                                  <path d={markerIcons.authorizedLocation.path} fill={markerIcons.authorizedLocation.fillColor} 
                                    stroke={markerIcons.authorizedLocation.strokeColor} strokeWidth="1" />
                                </svg>
                                <span style={{ marginLeft: '5px', fontSize: '12px' }}>
                                  Physical Location (Hover for address)
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                <svg height="24" width="24">
                                  <path d={markerIcons.checkinPin.path} fill={markerIcons.checkinPin.fillColor} 
                                    stroke={markerIcons.checkinPin.strokeColor} strokeWidth="1" />
                                </svg>
                                <span style={{ marginLeft: '5px', fontSize: '12px' }}>
                                  User Check-in
                                </span>
                              </div>
                              
                              {/* Proximity line information */}
                              <div style={{ marginTop: '8px', marginBottom: '3px' }}><strong>Proximity Lines:</strong></div>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: '3px', marginBottom: '2px' }}>
                                <div style={{ width: '15px', height: '3px', backgroundColor: '#21AA73' }}></div>
                                <span style={{ marginLeft: '5px', fontSize: '11px' }}>
                                  Within 100m of physical location
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: '3px', marginBottom: '2px' }}>
                                <div style={{ width: '15px', height: '3px', backgroundColor: '#F4BB44' }}></div>
                                <span style={{ marginLeft: '5px', fontSize: '11px' }}>
                                  Within 500m of physical location
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: '3px', marginBottom: '8px' }}>
                                <div style={{ width: '15px', height: '3px', backgroundColor: '#E6492E' }}></div>
                                <span style={{ marginLeft: '5px', fontSize: '11px' }}>
                                  More than 500m from physical location
                                </span>
                              </div>
                              
                              <div style={{ fontSize: '10px', marginTop: '5px', color: '#666' }}>
                                {clientLocations.filter(loc => loc.coordinates || loc.lat || loc.lng || loc.latitude || loc.longitude).length} physical locations displayed
                              </div>
                              <div style={{ fontSize: '10px', color: '#666' }}>
                                {filterCheckins().filter(c => c.coordinates).length} user check-ins displayed
                              </div>
                              <div style={{ fontSize: '10px', marginTop: '5px', fontStyle: 'italic', color: '#666' }}>
                                Click on a check-in to see distance to nearest location
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </GoogleMap>
                  )}
                </LoadScript>
              )}
            </CardBody>
          </Card>
          
          {viewMode === 'table' && (
            <Pagination
              data={filterCheckins()}
              pageSize={pageSize}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}      
            />
          )}

          {checkins.length === 0 && (
            <Row className="mt-4">
              <Col>
                <Card small>
                  <CardBody className="p-4 text-center">
                    <h4>No checkin data found</h4>
                    <p>There are no checkins in the date range you've selected.</p>
                    <div className="mb-2">
                      <Button theme="primary" onClick={createSampleCheckinData} className="mr-2">
                        Create Sample Data
                      </Button>
                      <Button theme="info" onClick={createTestCheckin} className="mr-2">
                        Create Test Check-in
                      </Button>
                      <Button theme="warning" onClick={createMultiFormatCheckin}>
                        Test Multiple Formats
                      </Button>
                    </div>
                    <div className="mt-3">
                      <small className="text-muted">
                        If coordinates or device types aren't showing, try the "Test Multiple Formats" button to test different data structures.
                      </small>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default Checkins; 